import { db } from '../firebase';
import { 
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { QueueManagerService } from './queueManagerService';
import { SystemSettingsService } from './systemSettingsService';
import { executeTemplatesV2 } from './templateSystemV2';
import { getOpenAISettings } from './openaiServiceV2';
import { getFieldValue } from '../utils/templateUtils';

const WORKFLOWS = 'workflows';
const WORKFLOW_EXECUTIONS = 'workflow_executions';
const TEMPLATES = 'templates';

export const WorkflowServiceV2 = {
  // Create a new workflow
  createWorkflow: async (userId, workflowData) => {
    const settings = await SystemSettingsService.getSettings();
    
    // Check workflow limit
    const userWorkflowsQuery = query(
      collection(db, WORKFLOWS),
      where('userId', '==', userId)
    );
    const userWorkflows = await getDocs(userWorkflowsQuery);
    
    if (userWorkflows.size >= settings.maxWorkflowsPerUser) {
      throw new Error('Maximum number of workflows reached');
    }

    // Validate workflow steps
    if (!workflowData.templateIds || workflowData.templateIds.length === 0) {
      throw new Error('Workflow must have at least one template');
    }
    
    if (workflowData.templateIds.length > settings.maxWorkflowSteps) {
      throw new Error('Maximum number of workflow steps exceeded');
    }

    // Verify all templates exist and user has access
    await Promise.all(workflowData.templateIds.map(async templateId => {
      const templateRef = doc(db, TEMPLATES, templateId);
      const templateDoc = await getDoc(templateRef);
      if (!templateDoc.exists() || templateDoc.data().userId !== userId) {
        throw new Error(`Template ${templateId} not found or access denied`);
      }
    }));

    const workflowRef = doc(collection(db, WORKFLOWS));
    const workflow = {
      id: workflowRef.id,
      userId,
      name: workflowData.name,
      description: workflowData.description || '',
      templates: workflowData.templateIds,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      version: 2 // Mark as V2 workflow
    };

    await setDoc(workflowRef, workflow);
    return workflow;
  },

  // Execute a workflow on multiple products
  executeWorkflow: async (userId, workflow, products, options = {}) => {
    const workflowId = typeof workflow === 'string' ? workflow : workflow.id;
    console.log('Executing workflow V2:', { workflowId, productCount: products.length });
    
    const settings = await SystemSettingsService.getSettings();
    
    // Check concurrent workflows limit
    const runningWorkflowsQuery = query(
      collection(db, WORKFLOW_EXECUTIONS),
      where('userId', '==', userId),
      where('status', '==', 'running')
    );
    const runningWorkflows = await getDocs(runningWorkflowsQuery);
    
    if (runningWorkflows.size >= settings.maxConcurrentWorkflows) {
      throw new Error('Maximum number of concurrent workflows reached');
    }

    // If workflow is a string (ID), fetch it from the database
    let workflowData;
    if (typeof workflow === 'string') {
      const workflowRef = doc(db, WORKFLOWS, workflow);
      const workflowDoc = await getDoc(workflowRef);
      if (!workflowDoc.exists() || workflowDoc.data().userId !== userId) {
        throw new Error('Workflow not found or access denied');
      }
      workflowData = workflowDoc.data();
    } else {
      // Use the provided workflow object
      workflowData = workflow;
    }

    // Create execution record
    const executionRef = doc(collection(db, WORKFLOW_EXECUTIONS));
    const executionId = executionRef.id;
    
    const execution = {
      id: executionId,
      workflowId: workflowData.id,
      workflowName: workflowData.name,
      userId,
      shopId: options.shopId || null,
      status: 'running',
      progress: 0,
      startTime: Timestamp.now(),
      endTime: null,
      productIds: products.map(p => p.toString()),
      productCount: products.length,
      completedProducts: 0,
      results: {},
      error: null,
      version: 2 // Mark as V2 execution
    };
    
    await setDoc(executionRef, execution);
    
    // Start execution in background
    setTimeout(async () => {
      try {
        const openAISettings = await getOpenAISettings(userId);
        const templates = workflowData.templates || [];
        const templateCount = templates.length;
        let currentTemplateIndex = 0;
        let executionResults = {};
        
        // Process each template in sequence
        for (const templateId of templates) {
          // Check if execution was cancelled
          const currentExecution = await getDoc(executionRef);
          if (!currentExecution.exists() || currentExecution.data().status === 'cancelled') {
            console.log('Workflow execution cancelled');
            return;
          }
          
          // Update progress
          const templateProgress = currentTemplateIndex / templateCount;
          await updateDoc(executionRef, { 
            progress: templateProgress,
            message: `Processing template ${currentTemplateIndex + 1} of ${templateCount}`
          });
          
          // Call progress callback
          if (options.onProgress) {
            options.onProgress({
              executionId,
              status: 'running',
              progress: templateProgress,
              message: `Processing template ${currentTemplateIndex + 1} of ${templateCount}`,
              results: executionResults
            });
          }
          
          // Execute template
          console.log(`Executing template ${currentTemplateIndex + 1}/${templateCount}: ${templateId}`);
          
          try {
            // Use QueueManagerService to manage concurrent API calls
            await QueueManagerService.addToQueue(async () => {
              const templateResults = await executeTemplatesV2(
                [templateId],
                products,
                (progress) => {
                  // Calculate overall progress
                  const overallProgress = (currentTemplateIndex + progress) / templateCount;
                  
                  // Update execution record
                  updateDoc(executionRef, { 
                    progress: overallProgress,
                    message: `Template ${currentTemplateIndex + 1}/${templateCount}: ${Math.round(progress * 100)}% complete`
                  });
                  
                  // Call progress callback
                  if (options.onProgress) {
                    options.onProgress({
                      executionId,
                      status: 'running',
                      progress: overallProgress,
                      message: `Template ${currentTemplateIndex + 1}/${templateCount}: ${Math.round(progress * 100)}% complete`,
                      results: executionResults
                    });
                  }
                },
                {
                  userId: userId, // Explicitly pass the userId
                  shopId: options.shopId,
                  getFieldValue: (product, field) => getFieldValue(product, field),
                  onComplete: (results) => {
                    console.log(`Template ${templateId} execution complete`);
                  }
                }
              );
              
              // Merge results
              executionResults = {
                ...executionResults,
                ...templateResults
              };
            });
          } catch (error) {
            console.error(`Error executing template ${templateId}:`, error);
            
            // Update execution with error
            await updateDoc(executionRef, {
              status: 'error',
              error: `Error in template ${currentTemplateIndex + 1}: ${error.message}`,
              endTime: Timestamp.now()
            });
            
            // Call progress callback with error
            if (options.onProgress) {
              options.onProgress({
                executionId,
                status: 'error',
                progress: (currentTemplateIndex + 0.5) / templateCount,
                message: `Error in template ${currentTemplateIndex + 1}: ${error.message}`,
                results: executionResults,
                error: error.message
              });
            }
            
            throw error;
          }
          
          currentTemplateIndex++;
        }
        
        // Update execution as completed
        await updateDoc(executionRef, {
          status: 'completed',
          progress: 1,
          results: executionResults,
          endTime: Timestamp.now(),
          completedProducts: products.length,
          message: 'Workflow execution completed successfully'
        });
        
        // Call completion callback
        if (options.onComplete) {
          options.onComplete({
            executionId,
            status: 'completed',
            progress: 1,
            results: executionResults,
            message: 'Workflow execution completed successfully'
          });
        }
        
        // Call progress callback with completion
        if (options.onProgress) {
          options.onProgress({
            executionId,
            status: 'completed',
            progress: 1,
            results: executionResults,
            message: 'Workflow execution completed successfully'
          });
        }
        
        return {
          executionId,
          results: executionResults
        };
      } catch (error) {
        console.error('Error executing workflow:', error);
        
        // Update execution with error if not already updated
        try {
          const currentExecution = await getDoc(executionRef);
          if (currentExecution.exists() && currentExecution.data().status === 'running') {
            await updateDoc(executionRef, {
              status: 'error',
              error: error.message,
              endTime: Timestamp.now()
            });
          }
        } catch (updateError) {
          console.error('Error updating execution status:', updateError);
        }
        
        throw error;
      }
    }, 0);
    
    // Return execution ID immediately
    return executionId;
  },

  // Get workflow execution status
  getExecutionStatus: async (userId, executionId) => {
    const executionRef = doc(db, WORKFLOW_EXECUTIONS, executionId);
    const executionDoc = await getDoc(executionRef);
    
    if (!executionDoc.exists() || executionDoc.data().userId !== userId) {
      throw new Error('Workflow execution not found or access denied');
    }
    
    return executionDoc.data();
  },

  // Cancel workflow execution
  cancelExecution: async (userId, executionId) => {
    const executionRef = doc(db, WORKFLOW_EXECUTIONS, executionId);
    
    await runTransaction(db, async (transaction) => {
      const executionDoc = await transaction.get(executionRef);
      
      if (!executionDoc.exists() || executionDoc.data().userId !== userId) {
        throw new Error('Workflow execution not found or access denied');
      }
      
      const execution = executionDoc.data();
      
      if (execution.status !== 'running' && execution.status !== 'queued') {
        throw new Error('Cannot cancel a completed or failed workflow');
      }
      
      transaction.update(executionRef, {
        status: 'cancelled',
        endTime: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      // Cancel any pending tasks
      // This will be handled by the QueueManagerService
    });
    
    return { success: true };
  }
};
